import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import ProfileCard from "./components/ProfileCard";
import StatsOverview from "./components/StatsOverview";
import OwnedBlocksList from "./components/OwnedBlocksList";
import BuildingsSummary from "./components/BuildingsSummary";
import IncomeChart from "./components/IncomeChart";
import { useLang } from "@/contexts/LanguageContext";

type Tab = "overview" | "blocks" | "buildings" | "income";

export default function MyPage() {
  const { user, openAuthModal } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLang();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const TABS: { id: Tab; label: string; icon: string }[] = [
    { id: "overview", label: t("tabOverview"), icon: "ri-dashboard-line" },
    { id: "blocks", label: t("tabBlocks"), icon: "ri-map-pin-2-line" },
    { id: "buildings", label: t("tabBuildings"), icon: "ri-building-2-line" },
    { id: "income", label: t("tabIncome"), icon: "ri-coin-line" },
  ];

  useEffect(() => {
    if (!user) {
      openAuthModal("login");
    }
  }, [user, openAuthModal]);

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const tabBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        {!user ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-amber-400/10 rounded-3xl mb-6">
              <i className="ri-lock-line text-4xl text-amber-400"></i>
            </div>
            <h2 className={`text-2xl font-black mb-2 ${textPrimary}`}>{t("loginRequired")}</h2>
            <p className={`text-sm mb-6 ${textSecondary}`}>{t("loginRequiredDesc")}</p>
            <button
              onClick={() => openAuthModal("login")}
              className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-full px-8 py-3 transition-all cursor-pointer whitespace-nowrap"
            >
              {t("loginBtn")}
            </button>
          </div>
        ) : (
          <>
            {/* Profile */}
            <ProfileCard />

            {/* Tabs */}
            <div className={`rounded-2xl border mb-6 overflow-hidden ${tabBg}`}>
              <div className="flex overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 border-b-2 ${
                      activeTab === tab.id
                        ? "border-amber-400 text-amber-400"
                        : isDark
                        ? "border-transparent text-gray-400 hover:text-white"
                        : "border-transparent text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    <i className={`${tab.icon} text-base`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
              <div>
                <StatsOverview />
                <IncomeChart />
                <OwnedBlocksList />
              </div>
            )}
            {activeTab === "blocks" && <OwnedBlocksList />}
            {activeTab === "buildings" && <BuildingsSummary />}
            {activeTab === "income" && <IncomeChart />}

            {/* Quick Action */}
            <div className={`mt-8 rounded-2xl border p-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${tabBg}`}>
              <div>
                <h3 className={`font-bold mb-1 ${textPrimary}`}>{t("buyMoreBlocks")}</h3>
                <p className={`text-sm ${textSecondary}`}>{t("buyMoreBlocksDesc")}</p>
              </div>
              <button
                onClick={() => navigate("/map")}
                className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-full px-8 py-3 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-map-line"></i>
                {t("goToMap")}
              </button>
            </div>
          </>
        )}
      </div>
      <AuthModal />
    </div>
  );
}
